/* eslint-disable import/no-internal-modules */
import { gql, useLazyQuery } from '@apollo/client';
import { Exception } from '@livecontrol/core-ui';
import type { Asset } from '@livecontrol/scheduler/model';
import type { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { normalize as normalizeEvent, StandardEventResponse } from '../event/dbx';
import type { QueryResult } from '../graphql';
import * as Dbx from './dbx';

interface TVariables {
  input: {
    limit?: number;
    page?: number;
    client_id?: number;
    date_gte?: DateTime;
    date_lte?: DateTime;
    origin?: string;
    location_id?: string;
    sort_field?: string;
    sort_order?: string;
    wildcard?: string;
    event_type?: string;
  };
}

interface TData {
  getAssetsPaginated: {
    assets?: (Dbx.Record & { event?: Dbx.Record })[];
    page?: number;
    has_more?: boolean;
    error?: string;
    message?: string;
  };
}

export const QUERY = gql`
  query GetAssetsPaginated($input: GetAssetsPaginatedInput) {
    getAssetsPaginated(input: $input) {
      error
      has_more
      page
      message
      assets {
        ...AssetPaginatedResponse
        event {
          ...StandardEventResponse
        }
      }
    }
  }

  ${Dbx.AssetPaginatedResponse}
  ${StandardEventResponse}
`;

interface Response {
  execute: (variables?: TVariables) => void;
  hasMore?: boolean;
  page?: number;
}

export const useLazyAssetsPaginated = (): QueryResult<Asset.withEvent[], 'assets'> & Response => {
  const [error, setError] = useState<Error | undefined>();
  const [assets, setAssets] = useState<Asset.withEvent[] | undefined>();
  const [hasMore, setHasMore] = useState<boolean | undefined>();
  const [page, setPage] = useState<number | undefined>();
  const [loading, setLoading] = useState(false);

  // Prepare the lazy query
  const [executeQuery, { data }] = useLazyQuery<TData, TVariables>(QUERY, {
    onError() {
      setError(Exception.KABOOM);
      setLoading(false);
    },
    onCompleted() {
      setLoading(false);
    }
  });

  // When available, parse server response
  useEffect(() => {
    let assets_;
    const paginatedAssets = data?.getAssetsPaginated.assets;

    if (paginatedAssets) {
      assets_ = paginatedAssets
        .map<Asset.withEvent | undefined>((record) => {
          const asset = Dbx.normalize(record);

          if (!asset) {
            return undefined;
          }

          const event = normalizeEvent(record.event);

          return { ...asset, eventData: event };
        })
        .filter((asset): asset is Asset.withEvent => !!asset);
    }

    setHasMore(data?.getAssetsPaginated.has_more ?? false);

    if (data?.getAssetsPaginated.page) {
      setPage(data.getAssetsPaginated.page);
    }

    setAssets(assets_);
  }, [data]);

  // Función manual para ejecutar la query
  const execute = (variables?: TVariables): void => {
    setLoading(true);
    setError(undefined);
    executeQuery({ variables });
  };

  return {
    loading,
    error,
    assets,
    page,
    hasMore,
    execute
  };
};
